import _ from "lodash";
import { saveAs } from "file-saver";
import { convertDataJSONToXLSX } from "modules/Services";
import {
  ERROR_CODES,
  OPERATION_TYPES,
  OPERATION_TYPE_TAB_CONFIG,
  COLUMN_LOCALE_MAP,
  BACKEND_HEADER_TO_KEY_MAP,
  getRowErrorCategory,
  ERROR_CELL_STYLE,
  MODIFIED_CELL_STYLE,
} from "./BulkUploadConstants";
import React from "react";
import moment from "moment";
import { formatDate } from "Utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

export const getErrorTitleReason = (errorCode, error, t) => {
  switch (errorCode) {
    case ERROR_CODES.EMPTY_SHEET:
      return t("common:no_data_found");
    case ERROR_CODES.NO_CHANGES_DETECTED:
      return t("transcript:no_changes_detected");
    case ERROR_CODES.INCORRECT_FILE_FORMAT:
      return t("transcript:invalid_file_format");
    case ERROR_CODES.MISSING_REQUIRED_COLUMN:
    case ERROR_CODES.DUPLICATE_COLUMNS:
    case ERROR_CODES.INVALID_COLUMN: {
      const columns = _.get(error, "columns", []);
      return t("transcript:count_error_detected", { count: _.size(columns) });
    }
    default:
      return "";
  }
};

export const getErrorAlertMessage = (errorCode, t) => {
  switch (errorCode) {
    case ERROR_CODES.EMPTY_SHEET:
      return t("transcript:empty_sheet_message");
    case ERROR_CODES.NO_CHANGES_DETECTED:
      return t("transcript:no_changes_message");
    case ERROR_CODES.INCORRECT_FILE_FORMAT:
      return t("transcript:invalid_format_message");
    default:
      return null;
  }
};

const getLocalizedColumnName = (key, fallbackHeader, t) => {
  const localeKey = COLUMN_LOCALE_MAP[key];
  if (localeKey) return t(localeKey);
  return fallbackHeader;
};

const getLocalizedBackendHeader = (backendHeader, t) => {
  const key = BACKEND_HEADER_TO_KEY_MAP[backendHeader];
  if (key) return getLocalizedColumnName(key, backendHeader, t);
  return backendHeader;
};

const getMessage = ({ errorCode, columnName, t }) => {
  switch (errorCode) {
    case ERROR_CODES.MISSING_REQUIRED_COLUMN:
      return t("transcript:column_not_found", { column: columnName });
    case ERROR_CODES.DUPLICATE_COLUMNS:
      return t("transcript:duplicate_column_found", { column: columnName });
    case ERROR_CODES.INVALID_COLUMN:
      return t("transcript:transcript_records_error_list_message_with_column", {
        column: columnName,
      });
    default:
      return "";
  }
};

export const getColumnErrorList = (error, t) => {
  const errorCode = _.get(error, "errorCode");
  const columns = _.get(error, "columns", []);

  return _.map(columns, (columnName, index) => {
    const localizedName = getLocalizedBackendHeader(columnName, t);

    return {
      id: `error-${index}`,
      message: getMessage({ errorCode, columnName: localizedName, t }),
    };
  });
};

export const hasColumnErrors = error => {
  const errorCode = _.get(error, "errorCode");
  return _.includes(
    [
      ERROR_CODES.MISSING_REQUIRED_COLUMN,
      ERROR_CODES.DUPLICATE_COLUMNS,
      ERROR_CODES.INVALID_COLUMN,
    ],
    errorCode
  );
};

const applyCustomStyles = ({ workSheet }) => {
  workSheet.getColumn(1).width = 71;

  const headerRow = workSheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "d9d9d9" },
  };

  return workSheet;
};

export const handleDownloadErrorList = async ({ columnErrorList, t }) => {
  try {
    const fileName = t("transcript:transcript_records_error_list");
    const sheetName = t("common:error_list");

    const dataRows = _.map(columnErrorList, errorItem => ({
      message: _.get(errorItem, "message", ""),
    }));

    const jsonSheetData = {
      filename: fileName,
      sheets: [
        {
          name: sheetName,
          rows: [
            {
              start: 1,
              dataRows: [
                [t("common:error_description")],
                ..._.map(dataRows, row => [row.message]),
              ],
            },
          ],
        },
      ],
    };

    const xlsxDataBlob = await convertDataJSONToXLSX({
      jsonSheetData,
      applyCustomStyles,
    });

    saveAs(xlsxDataBlob, `${fileName}.xlsx`);
  } catch (error) {
    console.error("Error downloading error list:", error);
  }
};

// --- Review Modal Helpers ---

export const getReviewDataTabs = ({ summary, t }) => {
  return _.reduce(
    OPERATION_TYPE_TAB_CONFIG,
    (acc, { type, labelKey, countKey }) => {
      const count = _.get(summary, countKey, 0);
      if (count > 0) {
        acc.push({
          value: type,
          label: t(labelKey),
          suffix: (
            <span
              style={{
                marginLeft: "-6px",
                color: "var(--text-secondary)",
              }}
            >
              ({count})
            </span>
          ),
        });
      }
      return acc;
    },
    []
  );
};

export const getReviewGridHeaders = ({ headers, operationType, t }) => {
  const DEFAULT_COLUMN_WIDTH = 160;

  const baseHeaders = _.map(headers, ({ key, header }) => ({
    id: key,
    title: getLocalizedColumnName(key, header, t),
    width: DEFAULT_COLUMN_WIDTH,
  }));

  if (operationType === OPERATION_TYPES.SKIP) {
    return [
      { id: "rowIndex", title: t("common:row"), width: 60 },
      { id: "error", title: t("common:error"), width: 260 },
      ...baseHeaders,
    ];
  }

  return baseHeaders;
};

const getCellValue = (data, key) => {
  const value = _.get(data, key, "-");
  if (value === "" || value === "-") return "-";

  const momentDate = moment(value, moment.ISO_8601, true);
  if (momentDate.isValid()) {
    return formatDate({
      date: momentDate,
      format: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
    });
  }

  return value;
};

const getRowErrorMessage = ({ errorType, columnName, t }) => {
  const category = getRowErrorCategory(errorType);
  switch (category) {
    case "empty":
      return t("transcript:left_empty_with_column", { column: columnName });
    case "duplicate":
      return t("transcript:duplicate_entry_found_error");
    default:
      return t("transcript:transcript_records_error_list_message_with_column", {
        column: columnName,
      });
  }
};

const buildErrorDropdownValues = ({ errorDetails, headers, headerMap, t }) => {
  const errorDetailsByFieldUid = _.keyBy(errorDetails, "fieldUid");

  return _.reduce(
    headers,
    (acc, { key }) => {
      const errorDetail = _.get(errorDetailsByFieldUid, key);
      if (errorDetail) {
        const columnName = _.get(headerMap, key, key);
        const label = getRowErrorMessage({
          errorType: errorDetail.type,
          columnName,
          t,
        });
        acc.push({ label, value: errorDetail.type });
      }
      return acc;
    },
    []
  );
};

export const getReviewGridData = ({ edges, headers, operationType, t }) => {
  const headerMap = _.reduce(
    headers,
    (acc, { key, header }) => {
      acc[key] = getLocalizedColumnName(key, header, t);
      return acc;
    },
    {}
  );

  return _.map(edges, edge => {
    const { rowIndex, rowPayload } = edge;

    if (operationType === OPERATION_TYPES.SKIP) {
      const rowDetails = _.get(rowPayload, "rowDetails", {});
      const errorDetails = _.get(rowPayload, "errorDetails", []);
      const errorFieldUids = _.map(errorDetails, "fieldUid");

      const errorDropdownValues = buildErrorDropdownValues({
        errorDetails,
        headers,
        headerMap,
        t,
      });

      const columns = [
        { value: rowIndex + 1, cellType: "text" },
        {
          value: errorDropdownValues,
          cellType: "dropdown",
          isMulti: true,
          isEditable: false,
          validInputs: { options: errorDropdownValues },
          showDropdownIcon: false,
        },
        ..._.map(headers, ({ key }) => {
          const isErrorField = _.includes(errorFieldUids, key);
          return {
            value: getCellValue(rowDetails, key),
            cellType: "text",
            ...(isErrorField ? { styles: ERROR_CELL_STYLE } : {}),
          };
        }),
      ];

      return { rowId: edge.id, columns };
    }

    if (operationType === OPERATION_TYPES.UPDATE) {
      const rowData = _.get(rowPayload, "rowData", {});
      const updatedFields = _.get(rowPayload, "updatedFields", []);
      const updatedFieldUids = _.map(updatedFields, "fieldUid");

      const columns = _.map(headers, ({ key }) => {
        const isUpdated = _.includes(updatedFieldUids, key);
        return {
          value: getCellValue(rowData, key),
          cellType: "text",
          ...(isUpdated ? { styles: MODIFIED_CELL_STYLE } : {}),
        };
      });

      return { rowId: edge.id, columns };
    }

    // CREATE
    const rowData = _.get(rowPayload, "rowData", {});
    const columns = _.map(headers, ({ key }) => ({
      value: getCellValue(rowData, key),
      cellType: "text",
    }));

    return { rowId: edge.id, columns };
  });
};
